import { defineNuxtPlugin, useNuxtApp } from '#app'
import { watch } from 'vue'
import { useFeatureFlags } from '../stores/featureFlags'

/**
 * Auto-initialize feature flags connection on app startup
 * This runs once when the Nuxt app is created (client-side only)
 * Non-blocking - runs in background
 *
 * Depends on Auth0 plugin being initialized first.
 * Awaits $auth0Ready() so that silent auth completes before we
 * attempt to connect — flags start loading in parallel with the
 * first route render, and unauthenticated users never trigger a
 * connection attempt.
 *
 * If auth hasn't settled by the time the plugin runs (post-login
 * redirect race), watches for isAuthenticated to become true and
 * initializes then.
 */
export default defineNuxtPlugin({
  name: 'rialtic-feature-flags',
  dependsOn: ['001-auth0'] as any,
  async setup() {
    const nuxtApp = useNuxtApp()
    const $auth0Ready = nuxtApp.$auth0Ready as
      | (() => Promise<unknown>)
      | undefined
    const $auth0 = nuxtApp.$auth0 as
      | (() => { isAuthenticated: { value: boolean } })
      | undefined

    if ($auth0Ready) await $auth0Ready()

    const { initialize } = useFeatureFlags()

    const tryInitialize = async () => {
      try {
        await initialize()
      } catch (error) {
        // Error already logged via Datadog in composables
        if (import.meta.dev) {
          console.warn('Feature flags initialization failed:', error)
        }
      }
    }

    if (!$auth0?.().isAuthenticated.value) {
      watch(
        () => $auth0?.().isAuthenticated.value ?? false,
        async (authed) => {
          if (authed) await tryInitialize()
        },
        { once: true },
      )
      return
    }

    await tryInitialize()
  },
})
